import { useEffect, useMemo } from 'react';

import { AgentDelegationKind, AgentDelegationTarget } from '@this+that/models';
import log from 'loglevel';

import {
  LocalMachineRowStatus,
  resolveLocalMachineRowStatus,
} from '../../pages/private/DoBox/components/DoBoxDelegateMenu/hooks/useDoBoxDelegateMenu';
import { useGetAgentDelegationTargets } from '../../services/agentDelegation.service';
import { useGetMyLocalDevices } from '../../services/localDevices.service';
import {
  McpConnection,
  useGetUserMcpConnections,
} from '../../services/mcpConnections.service';
import { useAgentDelegationTargetsStore } from '../../store/AgentDelegationTargets';
import { LOCAL_TARGETS_MAX_AGE_MS } from '../../utils/constants/agentDelegationTargets';

export interface DoBoxLocalClaudeAvailability {
  isLoading: boolean;
  /** True only while the local-machine probe is running for a paired machine
   *  that is connected — never for users without one. */
  isCheckingLocalMachine: boolean;
  /** The first SELECTABLE paired machine's delegation target — reusing
   *  resolveLocalMachineRowStatus (useDoBoxDelegateMenu.ts) so "selectable"
   *  never drifts from the delegate menu's own definition. Null when no
   *  device is paired, or every paired device is offline/erroring. */
  selectableTarget: AgentDelegationTarget | null;
  deviceName: string | null;
}

/**
 * Eager (not gated behind opening any menu) "is there a Local Claude I can
 * delegate to right now" check — used by the DoBox panel's deterministic
 * "Investigate & fix with Local Claude" suggestion and by the composer's
 * "To:" chip label/online dot, both of which need an answer before the user
 * opens any dropdown. useDoBoxDelegateMenu's own equivalent data only loads
 * once its menu is opened, so it is not reusable for this.
 */
export function useDoBoxLocalClaudeAvailability(
  enabled: boolean,
): DoBoxLocalClaudeAvailability {
  const { targets, fetchedAt, isLoading } = useAgentDelegationTargetsStore(
    (state) => state.slices[AgentDelegationKind.LOCAL_MCP_CODING],
  );
  const isLoadingTargets = isLoading && fetchedAt === null;
  const { getAgentDelegationTargets } = useGetAgentDelegationTargets();
  const { data: localDevicesData, loading: isLoadingDevices } =
    useGetMyLocalDevices({ skip: !enabled });
  const { data: mcpConnectionsData, loading: isLoadingConnections } =
    useGetUserMcpConnections({ skip: !enabled });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    useAgentDelegationTargetsStore
      .getState()
      .ensureFresh(
        AgentDelegationKind.LOCAL_MCP_CODING,
        () => getAgentDelegationTargets([AgentDelegationKind.LOCAL_MCP_CODING]),
        LOCAL_TARGETS_MAX_AGE_MS,
      )
      .catch((error) => {
        log.error(
          'useDoBoxLocalClaudeAvailability: failed to load targets',
          error,
        );
      });
  }, [enabled]);

  const devices = useMemo(
    () => localDevicesData?.getMyLocalDevices ?? [],
    [localDevicesData],
  );

  const connectionsById = useMemo(() => {
    const map = new Map<string, McpConnection>();
    for (const connection of mcpConnectionsData?.getUserMcpConnections ?? []) {
      map.set(connection.connectionId, connection);
    }
    return map;
  }, [mcpConnectionsData]);

  const targetsByConnectionId = useMemo(() => {
    const map = new Map<string, AgentDelegationTarget>();
    for (const target of targets) {
      if (target.kind === AgentDelegationKind.LOCAL_MCP_CODING) {
        map.set(target.id, target);
      }
    }
    return map;
  }, [targets]);

  const selectable = useMemo(() => {
    for (const device of devices) {
      const connection = device.mcpConnectionId
        ? connectionsById.get(device.mcpConnectionId)
        : undefined;
      const target = device.mcpConnectionId
        ? (targetsByConnectionId.get(device.mcpConnectionId) ?? null)
        : null;
      const status = resolveLocalMachineRowStatus(device, target, connection);
      if (status === LocalMachineRowStatus.SELECTABLE && target) {
        return { target, deviceName: connection?.name ?? device.name };
      }
    }
    return null;
  }, [devices, connectionsById, targetsByConnectionId]);

  const hasConnectedMachine = devices.some(
    (device) => device.isConnected && Boolean(device.mcpConnectionId),
  );

  return {
    isLoading:
      enabled && (isLoadingTargets || isLoadingDevices || isLoadingConnections),
    isCheckingLocalMachine: enabled && isLoadingTargets && hasConnectedMachine,
    selectableTarget: selectable?.target ?? null,
    deviceName: selectable?.deviceName ?? null,
  };
}
